import { useRouter } from "next/router";

import useSearchProducts from "@/Apollo/hooks/useSearchProducts";
import { SearchProductsByQueryItem } from "@/common/contracts/home.contracts";
import { useLanguage } from "@/common/hooks/pages.hooks";
import { isUsLocale } from "@/i18n/config";
import type { Language, SearchProductsQuery } from "@whoppah/apollo/schema";

/** Two desktop rows of six cards. */
const FOR_YOU_LIMIT = 6;
/**
 * WHO-6510: on `/en-us` the block asks for more and keeps the first six that ship to the US. The
 * gateway's search has no US filter, and recommendations ship far less often than the catalogue:
 * 4 of 18 and 12 of 48 on production (3 Nov 2026), at the same response time. 48 keeps six.
 */
const FOR_YOU_US_FETCH_LIMIT = 48;
const NO_PRODUCTS: SearchProductsByQueryItem[] = [];

/**
 * The block's query variables. Exported so the homepage's "New this week" can read the same Apollo
 * cache entry (cache-only) to leave out what this block already shows.
 */
export const homepageForYouVariables = (
  language: Language,
  usMarket = false
) => ({
  input: {
    recommended: true,
    pagination: { limit: usMarket ? FOR_YOU_US_FETCH_LIMIT : FOR_YOU_LIMIT },
  },
  lang: language,
  ...(usMarket ? { usMarket: true } : {}),
});

/**
 * The cards the block shows: the gateway can return nulls in the list, and on `/en-us` only what
 * ships to the US, six at most. Shared with "New this week", which leaves these out.
 */
export const homepageForYouItems = (
  items:
    | NonNullable<SearchProductsQuery["searchProducts"]>["items"]
    | undefined,
  usMarket = false
): SearchProductsByQueryItem[] => {
  const products =
    items?.filter(
      (product): product is SearchProductsByQueryItem => !!product
    ) ?? NO_PRODUCTS;

  return usMarket
    ? products
        .filter(product => product.usShippable === true)
        .slice(0, FOR_YOU_LIMIT)
    : products;
};

export const useHomepageForYouProducts = () => {
  const { language } = useLanguage();
  const usMarket = isUsLocale(useRouter().locale);

  const { data, loading } = useSearchProducts({
    variables: homepageForYouVariables(language, usMarket),
  });

  const products = homepageForYouItems(data?.searchProducts?.items, usMarket);
  const searchMeta = {
    queryId: data?.searchProducts?.queryID ?? undefined,
    indexName: data?.searchProducts?.indexName ?? undefined,
    page: data?.searchProducts?.pagination?.page,
    limit: data?.searchProducts?.pagination?.limit,
  };

  /*
   * On `/en-us` the block goes, heading included, once the answer leaves nothing that ships: a
   * shopper whose recommendations are all from sellers who don't ship, or a failed call. Every
   * other locale keeps the block as it was.
   */
  const hidden = usMarket && !loading && products.length === 0;

  return { products, searchMeta, hidden };
};
